import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * ClinicContext — single source of truth for the clinic's identity
 * (name, address, phone, email, GSTIN, logo, etc.).
 *
 * Use via `useClinic()` anywhere in the app so that print headers,
 * WhatsApp messages, and UI labels all stay in sync with what the
 * admin has configured in Settings → Clinic Info.
 *
 * Also exports `buildPrintHeaderHTML(clinic, mode)` — returns the
 * HTML fragment you can paste at the top of any print-window document.
 */

const DEFAULT_CLINIC = {
  clinicName: 'Your Clinic',
  clinicShort: 'CL',
  clinicAddress: '',
  clinicPhone: '',
  clinicEmail: '',
  clinicGstin: '',
  headerNote: '',
  footerNote: '',
  logo: '',
};

const ClinicContext = createContext({
  clinic: DEFAULT_CLINIC,
  loading: true,
  refreshClinic: () => {},
});

export const ClinicProvider = ({ API_BASE, children }) => {
  const [clinic, setClinic] = useState(DEFAULT_CLINIC);
  const [loading, setLoading] = useState(true);

  const refreshClinic = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/admin/clinic-settings`);
      if (r.data && typeof r.data === 'object') {
        setClinic({ ...DEFAULT_CLINIC, ...r.data });
      }
    } catch (e) { /* silent — keep defaults */ }
    setLoading(false);
  }, [API_BASE]);

  useEffect(() => { refreshClinic(); }, [refreshClinic]);

  return (
    <ClinicContext.Provider value={{ clinic, loading, refreshClinic }}>
      {children}
    </ClinicContext.Provider>
  );
};

export const useClinic = () => useContext(ClinicContext);

/**
 * Build the standard clinic letterhead HTML for printable documents.
 *
 * @param {object} clinic  clinic settings from useClinic()
 * @param {'plain'|'letterhead'} mode
 *        'plain'      → draws a full header with name + address + phone
 *        'letterhead' → leaves an 80-90px top gap (pre-printed letterhead)
 */
export const buildPrintHeaderHTML = (clinic, mode = 'plain') => {
  if (mode === 'letterhead') return '<div style="height:90px"></div>';
  const name = escapeHtml(clinic?.clinicName || 'Your Clinic');
  const addr = escapeHtml(clinic?.clinicAddress || '');
  const phone = escapeHtml(clinic?.clinicPhone || '');
  const email = escapeHtml(clinic?.clinicEmail || '');
  const gstin = escapeHtml(clinic?.clinicGstin || '');
  const contact = [phone && `Ph: ${phone}`, email, gstin && `GSTIN: ${gstin}`].filter(Boolean).join(' &nbsp;|&nbsp; ');
  return `
    <div style="text-align:center;padding:10px 0 12px;border-bottom:2px solid #222;margin-bottom:14px;">
      <div style="font-size:20px;font-weight:900;letter-spacing:1.5px;text-transform:uppercase;color:#111;">${name}</div>
      ${addr ? `<div style="font-size:10.5px;color:#555;margin-top:3px;">${addr}</div>` : ''}
      ${contact ? `<div style="font-size:11px;color:#333;font-weight:600;margin-top:4px;">${contact}</div>` : ''}
    </div>
  `;
};

/**
 * Plain-text version for UI/WhatsApp. `mode` ignored.
 */
export const clinicHeaderText = (clinic) => {
  const parts = [
    clinic?.clinicName,
    clinic?.clinicAddress,
    clinic?.clinicPhone ? `Ph: ${clinic.clinicPhone}` : null,
  ].filter(Boolean);
  return parts.join(' · ');
};

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
